export type GameParsingStatus = 'started' | 'completed' | 'failed' | string;

export type GameParsingOutcome = 'complete' | 'partial' | 'fatal';

export type GameParsingProviderStatus = 'active' | 'inactive';

export type GameParsingProviderResult = {
  providerId: number;
  providerName: string;
  status: GameParsingProviderStatus;
  currenciesCount: number;
  gamesCount: number;
};

export type GameParsingRun = {
  runId: string;
  status: GameParsingStatus;
  outcome?: GameParsingOutcome;
  total: number;
  new: number;
  updated: number;
  unchanged: number;
  existing: number;
  requestFailures: number;
  gameFailures: number;
  providers?: GameParsingProviderResult[];
  followedFrom?: string;
};

export const PARSING_RUN_STORAGE_KEY = 'game-center-parsing-run';

const RUN_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type RunStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

function toNonNegativeInt(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return 0;
  }
  return Math.floor(n);
}

function isTerminal(status: string): boolean {
  return status === 'completed' || status === 'failed';
}

function readOutcome(status: string, value: unknown): GameParsingOutcome | undefined {
  if (status === 'completed' && (value === 'complete' || value === 'partial')) {
    return value;
  }
  if (status === 'failed' && value === 'fatal') {
    return 'fatal';
  }
  return undefined;
}

function normalizeProviders(value: unknown): GameParsingProviderResult[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const byId = new Map<number, GameParsingProviderResult>();
  for (const row of value) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const record = row as Record<string, unknown>;
    const providerId = Number(record.providerId);
    if (!Number.isInteger(providerId) || providerId <= 0) {
      continue;
    }
    byId.set(providerId, {
      providerId,
      providerName: typeof record.providerName === 'string' ? record.providerName.trim() : '',
      status: record.status === 'active' ? 'active' : 'inactive',
      currenciesCount: toNonNegativeInt(record.currenciesCount),
      gamesCount: toNonNegativeInt(record.gamesCount),
    });
  }
  return Array.from(byId.values()).sort((left, right) => left.providerId - right.providerId);
}

export function normalizeGameParsingRun(raw: unknown): GameParsingRun | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const payload = raw as Record<string, unknown>;
  const runId = typeof payload.runId === 'string' ? payload.runId.trim() : '';
  if (!runId) {
    return null;
  }
  const total = toNonNegativeInt(payload.total);
  const created = toNonNegativeInt(payload.new);
  const safeNew = created > total ? total : created;
  const updated = toNonNegativeInt(payload.updated);
  const unchanged = toNonNegativeInt(payload.unchanged);
  const hasExisting = Object.prototype.hasOwnProperty.call(payload, 'existing');
  const existing = hasExisting
    ? toNonNegativeInt(payload.existing)
    : payload.updated != null || payload.unchanged != null
      ? updated + unchanged
      : Math.max(0, total - safeNew);
  const status = typeof payload.status === 'string' && payload.status.trim()
    ? payload.status.trim()
    : 'started';
  const providers = normalizeProviders(payload.providers);
  const outcome = readOutcome(status, payload.outcome);
  const run: GameParsingRun = {
    runId,
    status,
    total,
    new: safeNew,
    updated,
    unchanged,
    existing,
    requestFailures: toNonNegativeInt(payload.requestFailures),
    gameFailures: toNonNegativeInt(payload.gameFailures),
    ...(outcome ? { outcome } : {}),
    ...(providers ? { providers } : {}),
  };
  return run;
}

function mergeProgress(current: GameParsingRun | null, incoming: GameParsingRun): GameParsingRun {
  if (!current || current.runId !== incoming.runId) {
    return incoming;
  }
  if (isTerminal(current.status) && !isTerminal(incoming.status)) {
    return current;
  }
  if (isTerminal(current.status) && isTerminal(incoming.status) && current.status !== incoming.status) {
    return current;
  }
  const total = Math.max(current.total, incoming.total);
  const created = Math.max(current.new, incoming.new);
  const merged: GameParsingRun = {
    runId: incoming.runId,
    status: isTerminal(current.status) ? current.status : incoming.status,
    total,
    new: created > total ? total : created,
    updated: Math.max(current.updated, incoming.updated),
    unchanged: Math.max(current.unchanged, incoming.unchanged),
    existing: Math.max(current.existing, incoming.existing),
    requestFailures: Math.max(current.requestFailures, incoming.requestFailures),
    gameFailures: Math.max(current.gameFailures, incoming.gameFailures),
  };
  const outcome = isTerminal(current.status) ? current.outcome : incoming.outcome;
  if (outcome) {
    merged.outcome = outcome;
  }
  if (incoming.providers) {
    merged.providers = incoming.providers;
  } else if (current.providers) {
    merged.providers = current.providers;
  }
  if (current.followedFrom) {
    merged.followedFrom = current.followedFrom;
  }
  return merged;
}

function readReplacementRunId(raw: unknown): string {
  if (!raw || typeof raw !== 'object') {
    return '';
  }
  const value = (raw as Record<string, unknown>).replacementRunId;
  if (typeof value !== 'string' || !RUN_ID_RE.test(value.trim())) {
    return '';
  }
  return value.trim();
}

/**
 * Apply one progress event. Status comes only from the payload status.
 * A replacement run id switches the page off the interrupted run.
 */
export function resolveParsingProgress(
  current: GameParsingRun | null,
  raw: unknown,
): GameParsingRun | null {
  const incoming = normalizeGameParsingRun(raw);
  if (!incoming) {
    return current;
  }
  const replacementRunId = readReplacementRunId(raw);
  if (replacementRunId && replacementRunId !== incoming.runId) {
    if (current?.runId === replacementRunId) {
      return current;
    }
    return {
      runId: replacementRunId,
      status: 'started',
      total: 0,
      new: 0,
      updated: 0,
      unchanged: 0,
      existing: 0,
      requestFailures: 0,
      gameFailures: 0,
      followedFrom: incoming.runId,
    };
  }
  if (
    current?.followedFrom
    && incoming.runId === current.followedFrom
    && current.runId !== incoming.runId
  ) {
    return current;
  }
  return mergeProgress(current, incoming);
}

export function parsingStatusLabelKey(run: Pick<GameParsingRun, 'status' | 'outcome'>): string {
  if (run.status === 'completed' && run.outcome === 'partial') {
    return 'parsingStatusCompletedWithErrors';
  }
  if (run.status === 'completed') {
    return 'parsingStatusCompleted';
  }
  if (run.status === 'failed') {
    return 'parsingStatusFailed';
  }
  return 'parsingStatusInProgress';
}

export function parsingStatusDefaultLabel(run: Pick<GameParsingRun, 'status' | 'outcome'>): string {
  if (run.status === 'completed' && run.outcome === 'partial') {
    return 'Completed with errors';
  }
  if (run.status === 'completed') {
    return 'Completed';
  }
  if (run.status === 'failed') {
    return 'Failed';
  }
  return 'In progress';
}

export function browserSessionStorage(): RunStorage | null {
  try {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    return sessionStorage;
  } catch (_err) {
    return null;
  }
}

export function readStoredParsingRun(storage: Pick<RunStorage, 'getItem'> | null): GameParsingRun | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(PARSING_RUN_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    const run = normalizeGameParsingRun(parsed);
    if (!run || !parsed || typeof parsed !== 'object') {
      return run;
    }
    const followedFrom = (parsed as Record<string, unknown>).followedFrom;
    if (typeof followedFrom === 'string' && followedFrom.trim() && followedFrom.trim() !== run.runId) {
      run.followedFrom = followedFrom.trim();
    }
    return run;
  } catch (_err) {
    return null;
  }
}

export function writeStoredParsingRun(
  storage: RunStorage | null,
  run: GameParsingRun | null,
): void {
  if (!storage) {
    return;
  }
  try {
    if (!run) {
      storage.removeItem(PARSING_RUN_STORAGE_KEY);
      return;
    }
    storage.setItem(PARSING_RUN_STORAGE_KEY, JSON.stringify(run));
  } catch (_err) {
    // Ignore private-mode and quota failures. The live socket remains the source.
  }
}
